<template>
    <b-card :class="['app-card', cardClass]" no-body>
        <b-card-header
            :class="['app-card-header', { 'app-card-header-clickable': collapsible && headerClickable }]"
            @click="collapsible && headerClickable && toggleCollapse()"
        >
            <span class="header-content">
                <slot name="header">
                    <div class="header-text">
                        <h5 v-if="!useH6" class="mb-0">{{ title }}</h5>
                        <h6 v-else class="mb-0">{{ title }}</h6>
                        <small v-if="subtitle" class="text-muted subtitle">{{ subtitle }}</small>
                    </div>
                </slot>
            </span>

            <span class="header-actions d-flex align-items-center">
                <slot name="header-actions"></slot>

                <font-awesome-icon
                    v-if="collapsible"
                    size="lg"
                    class="ml-2 text-primary collapse-icon"
                    :icon="['fas', collapsed ? 'circle-chevron-down' : 'circle-chevron-up']"
                />
            </span>
        </b-card-header>

        <b-collapse :visible="!collapsed">
            <b-card-body :class="['app-card-body', bodyClass]">
                <slot></slot>
            </b-card-body>

            <b-card-footer v-if="$slots.footer || $scopedSlots.footer" :class="['app-card-footer', footerClass]">
                <slot name="footer"></slot>
            </b-card-footer>
        </b-collapse>
    </b-card>
</template>

<script>
    export default {
        name: 'FormCard',
        props: {
            title: {
                type: String,
                default: '',
            },
            subtitle: {
                type: String,
                default: '',
            },
            collapsible: {
                type: Boolean,
                default: true,
            },
            initialCollapsed: {
                type: Boolean,
                default: false,
            },
            headerClickable: {
                type: Boolean,
                default: true,
            },
            cardClass: {
                type: String,
                default: '',
            },
            bodyClass: {
                type: String,
                default: '',
            },
            footerClass: {
                type: String,
                default: '',
            },
            useH6: {
                type: Boolean,
                default: false,
            },
            cardId: {
                type: String,
                default: null,
            },
        },
        data() {
            return {
                collapsed: this.initialCollapsed,
            };
        },
        methods: {
            toggleCollapse() {
                if (!this.collapsible) return;

                this.collapsed = !this.collapsed;
                this.$emit('toggle', this.collapsed);
                this.$emit('update:collapsed', this.collapsed);
            },

            expand() {
                this.collapsed = false;
                this.$emit('toggle', false);
                this.$emit('update:collapsed', false);
            },

            collapse() {
                this.collapsed = true;
                this.$emit('toggle', true);
                this.$emit('update:collapsed', true);
            },
        },
        watch: {
            initialCollapsed(newVal) {
                this.collapsed = newVal;
            },
        },
    };
</script>

<style lang="scss" scoped>
    $brand-color: #0d47a1;
    $brand-color-light: #1976d2;
    $border-color: #f0f0f0;
    $background-color: #ffffff;
    $card-border-color: #e9ecef;

    .app-card {
        border: 1px solid $card-border-color !important;
        overflow: hidden !important;
        margin-bottom: 1.5rem !important;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        background-color: $background-color !important;
        box-shadow: none !important;

        &:last-of-type {
            margin-bottom: 2rem !important;
        }
    }

    .app-card-header {
        padding: 1.25rem 1.5rem !important;
        background-color: $background-color !important;
        border-bottom: 1px solid $border-color !important;
        transition: background-color 0.2s ease;

        h5 {
            font-size: 1.125rem !important;
            font-weight: 600 !important;
            color: $brand-color !important;
            margin: 0 !important;
            letter-spacing: -0.02em;
        }

        h6 {
            font-size: 1.125rem !important;
            font-weight: 600 !important;
            color: $brand-color !important;
            margin: 0 !important;
        }

        &.app-card-header-clickable {
            cursor: pointer;

            &:hover {
                background-color: rgba($brand-color, 0.02) !important;
            }
        }

        .text-primary,
        .collapse-icon {
            color: $brand-color !important;
            transition: transform 0.2s ease;
        }
    }

    .app-card-body {
        padding: 1.75rem !important;
        background-color: $background-color !important;
    }

    .app-card-footer {
        padding: 1.25rem 1.5rem !important;
        border-top: 1px solid $border-color !important;

        &:empty {
            display: none;
        }
    }

    .header-content {
        flex: 1;
        display: flex;
        align-items: center;
    }

    .header-actions {
        flex-shrink: 0;
    }

    .header-text {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
    }

    .subtitle {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.2;
        display: block;
        margin-top: 0.125rem;
    }

    :deep(.collapse-transition) {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    @media (max-width: 768px) {
        .app-card-body {
            padding: 1.25rem !important;
        }

        .app-card-header {
            padding: 1rem 1.25rem !important;
        }

        .app-card-footer {
            padding: 1rem 1.25rem !important;
        }

        .subtitle {
            font-size: 0.8125rem;
        }
    }

    #email-card.app-card {
        margin-top: 1.5rem !important;
    }

    #email-card-header.app-card-header {
        padding: 1rem 1.5rem !important;
    }

    #email-card-body.app-card-body {
        padding: 1.5rem !important;
    }
</style>

<style>
    .vue-app-wrapper .card.app-card {
        border: 1px solid #e9ecef !important;
    }

    .vue-app-wrapper .card.app-card:hover {
        border-color: #dee2e6 !important;
    }
</style>
